import { DatePipe, LowerCasePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewContainerRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { CDN_URL, LeadStatus } from '@maximizer/core/shared/domain';
import { UI_BUTTONS } from '@maximizer/core/shared/ui/components/buttons';
import { EntrySearchService } from '@maximizer/outlook/main/data-access';
import { OutlookStore } from '@maximizer/outlook/shared/data-access';
import {
  EntrySearchGroupType,
  EntrySearchResult,
  EntrySearchResultGroup,
  EntryType,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_TIME,
} from '@maximizer/outlook/shared/domain';
import {
  MenuItem,
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';
import {
  EMPTY,
  Subject,
  catchError,
  debounce,
  fromEvent,
  of,
  switchMap,
  timer,
} from 'rxjs';

const SINGLE_GROUP_DISPLAY_COUNT = 5;
const MULTI_GROUP_DISPLAY_COUNT = 3;
const GROUP_DISPLAY_INCREMENT = 20;
const SKELETON_GROUP_COUNT = 2;

const SCROLL_CONTAINER_SELECTOR = '.scrollbar';
const SCROLL_TOP_THRESHOLD = 100;

const GROUP_ICONS: Record<EntrySearchGroupType, string> = {
  individuals: 'maximizer-icons icon-individual',
  contacts: 'fa-icons icon icon-solid icon-user',
  companies: 'fa-icons icon icon-regular icon-building',
  leads: 'fa-icons icon icon-solid icon-user-magnifying-glass',
};

const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  [LeadStatus.Unqualified]: 'outlook.entrySearch.leadStatus.unqualified',
  [LeadStatus.Qualified]: 'outlook.entrySearch.leadStatus.qualified',
  [LeadStatus.Converted]: 'outlook.entrySearch.leadStatus.converted',
  [LeadStatus.Archived]: 'outlook.entrySearch.leadStatus.archived',
};

const LEAD_STATUS_CHIP_CLASSES: Record<LeadStatus, string> = {
  [LeadStatus.Unqualified]: 'bg-neutral-20 text-neutral-90',
  [LeadStatus.Qualified]: 'bg-inserv-10 text-inserv-140',
  [LeadStatus.Converted]: 'bg-finserv-10 text-finserv-140',
  [LeadStatus.Archived]: 'bg-error-10 text-error-140',
};

const DEFAULT_ACTION_ICON = 'fa-icons icon-ellipsis-vertical icon-16 px-1';

type SearchRequest = { term: string; isImmediate: boolean };

@Component({
  selector: 'maximizer-outlook-entry-search',
  templateUrl: './entry-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DatePipe,
    KENDO_BUTTONS,
    KENDO_INPUTS,
    KENDO_TOOLTIPS,
    LowerCasePipe,
    OUTLOOK_SHARED_UI,
    TranslatePipe,
    UI_BUTTONS,
  ],
})
export class OutlookEntrySearchComponent implements OnInit {
  readonly viewContainerRef = inject(ViewContainerRef);
  readonly cdn = inject(CDN_URL);

  notification = viewChild<OutlookNotificationComponent>('notification');

  entryActions = input.required<(entry: EntrySearchResult) => MenuItem[]>();
  disabled = input(false);
  restoreSearchOnLoad = input(true);

  searchActiveChange = output<boolean>();

  private readonly entrySearchService = inject(EntrySearchService);
  private readonly globalStore = inject(GlobalStore);
  private readonly outlookStore = inject(OutlookStore);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private readonly searchTerm = new Subject<SearchRequest>();
  private readonly actionsRevision = signal(0);

  private readonly initialDisplayCount = computed(() =>
    this.resultGroups().length > 1
      ? MULTI_GROUP_DISPLAY_COUNT
      : SINGLE_GROUP_DISPLAY_COUNT,
  );

  private scrollContainer: HTMLElement | null = null;
  private isRestoringDisplayCounts = false;

  searchPhrase = signal('');
  trimmedSearchPhrase = computed(() => this.searchPhrase().trim());
  isPanelOpen = signal(false);
  isLoadingResults = signal(false);
  resultGroups = signal<EntrySearchResultGroup[]>([]);
  displayCounts = signal<Partial<Record<EntrySearchGroupType, number>>>({});
  scrollToTopVisible = signal(false);

  totalResultCount = computed(() =>
    this.resultGroups().reduce(
      (total, group) => total + group.results.length,
      0,
    ),
  );

  displayGroups = computed(() => {
    this.actionsRevision();

    return this.resultGroups().map((group) => {
      const displayCount =
        this.displayCounts()[group.type] ?? this.initialDisplayCount();
      return {
        type: group.type,
        totalCount: group.results.length,
        hasMore: group.results.length > displayCount,
        rows: group.results.slice(0, displayCount).map((entry) => {
          const actions = this.entryActions()(entry);

          return {
            entry,
            title: this.viewEntryTitle(entry),
            companyName: this.secondaryCompanyName(entry),
            status:
              entry.leadStatus === undefined
                ? null
                : {
                    label: LEAD_STATUS_LABELS[entry.leadStatus],
                    chipClass: LEAD_STATUS_CHIP_CLASSES[entry.leadStatus],
                  },
            actions,
            singleAction:
              actions.length === 1 ? this.toActionButton(actions[0]) : null,
          };
        }),
      };
    });
  });

  EntryType = EntryType;
  groupIcons = GROUP_ICONS;

  skeletonGroups = Array.from(
    { length: SKELETON_GROUP_COUNT },
    (_, index) => index,
  );

  constructor() {
    this.searchTerm
      .pipe(
        debounce(({ isImmediate }) =>
          timer(isImmediate ? 0 : SEARCH_DEBOUNCE_TIME),
        ),
        switchMap(({ term }) => {
          if (!term) {
            return EMPTY;
          }
          return this.entrySearchService
            .searchEntries(term, this.canReadLeads())
            .pipe(
              catchError(() => {
                this.notification()?.show('load', 'error');
                return of([] as EntrySearchResultGroup[]);
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((groups) => {
        this.resultGroups.set(groups);
        this.isLoadingResults.set(false);

        if (this.isRestoringDisplayCounts) {
          this.isRestoringDisplayCounts = false;
          this.displayCounts.set(this.outlookStore.entrySearchDisplayCounts());
        }
      });
  }

  ngOnInit(): void {
    this.watchHostScroll();

    const preservedPhrase = this.outlookStore.entrySearchPhrase();

    if (
      preservedPhrase === null ||
      this.disabled() ||
      !this.restoreSearchOnLoad()
    ) {
      return;
    }

    this.searchPhrase.set(preservedPhrase);
    this.isRestoringDisplayCounts = true;
    // The debounce throttles typing; a restored phrase is already final.
    this.runSearch({ term: preservedPhrase, isImmediate: true });
  }

  handleSearchPhraseChange(inputText: string): void {
    this.searchPhrase.set(inputText);
    const trimmedTerm = inputText.trim();

    if (trimmedTerm.length < MIN_SEARCH_LENGTH) {
      this.outlookStore.clearEntrySearchContext();
      this.resultGroups.set([]);
      this.displayCounts.set({});
      this.isLoadingResults.set(false);
      this.closePanel();
      this.searchTerm.next({ term: '', isImmediate: true });
      return;
    }

    this.outlookStore.setEntrySearchContext(trimmedTerm);
    this.outlookStore.setEntrySearchDisplayCounts({});
    this.runSearch({ term: trimmedTerm, isImmediate: false });
  }

  showMore(groupType: EntrySearchGroupType): void {
    this.displayCounts.update((counts) => ({
      ...counts,
      [groupType]:
        (counts[groupType] ?? this.initialDisplayCount()) +
        GROUP_DISPLAY_INCREMENT,
    }));
    this.outlookStore.setEntrySearchDisplayCounts(this.displayCounts());
  }

  scrollToTop(): void {
    this.scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  openEntryDetails(entry: EntrySearchResult): void {
    this.entrySearchService.trackEntryDetailsOpened(entry);
    this.router.navigate([`/${entry.type}/${entry.id}`]);
  }

  refreshActions(): void {
    this.actionsRevision.update((revision) => revision + 1);
  }

  private runSearch(request: SearchRequest): void {
    this.isLoadingResults.set(true);
    this.resultGroups.set([]);
    this.displayCounts.set({});
    this.openPanel();
    this.searchTerm.next(request);
  }

  private watchHostScroll(): void {
    this.scrollContainer = (
      this.elementRef.nativeElement as HTMLElement
    ).closest(SCROLL_CONTAINER_SELECTOR);

    if (!this.scrollContainer) {
      return;
    }

    const container = this.scrollContainer;
    fromEvent(container, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.scrollToTopVisible.set(container.scrollTop > SCROLL_TOP_THRESHOLD),
      );
  }

  private toActionButton(action: MenuItem) {
    return {
      ...action,
      iconClass: action.icon ?? DEFAULT_ACTION_ICON,
      tooltip: action.title ?? action.text,
    };
  }

  private secondaryCompanyName(entry: EntrySearchResult): string {
    return entry.companyName === entry.displayName ? '' : entry.companyName;
  }

  private viewEntryTitle(entry: EntrySearchResult): string {
    return this.translate.instant('outlook.main.tooltip.view-entry', {
      type: this.entryTypeLabel(entry),
    });
  }

  private entryTypeLabel(entry: EntrySearchResult): string {
    return this.entrySearchService.entryTypeLabel(
      entry.type,
      entry.abentryType,
    );
  }

  private canReadLeads(): boolean {
    return !!this.globalStore.session()?.user?.rights?.lead?.read;
  }

  private openPanel(): void {
    if (!this.isPanelOpen()) {
      this.isPanelOpen.set(true);
      this.searchActiveChange.emit(true);
    }
  }

  private closePanel(): void {
    if (this.isPanelOpen()) {
      this.isPanelOpen.set(false);
      this.searchActiveChange.emit(false);
    }
  }
}
